import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/luxenova/Navbar";
import Footer from "@/components/luxenova/Footer";
import ChatbotWidget from "@/components/luxenova/ChatbotWidget";
import useSeo from "@/lib/useSeo";
import { useI18n } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
    const { slug } = useParams();
    const { t } = useI18n();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await fetch(`${API}/blog/posts/${slug}`);
            if (!res.ok) {
                setError("not_found");
                return;
            }
            setPost(await res.json());
        })();
    }, [slug]);

    useSeo({
        title: post ? (post.seo_title || post.title) + " — LuxeNova" : "LuxeNova",
        description: post ? post.seo_description || post.excerpt : undefined,
        image: post?.featured_image,
        canonical: typeof window !== "undefined" ? window.location.href : undefined,
    });

    return (
        <div className="min-h-screen bg-[#050D1C] text-slate-200">
            <Navbar />
            <main className="pt-28 pb-24">
                <div className="max-w-3xl mx-auto px-6 md:px-8">
                    <Link
                        to="/blog"
                        data-testid="blogpost-back"
                        className="text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] hover:text-white transition"
                    >
                        {t("blog.nav_back")}
                    </Link>

                    {error && (
                        <div className="mt-24 text-center">
                            <div className="font-serif-display text-white text-3xl">404</div>
                            <p className="mt-3 text-slate-400">Post not found.</p>
                            <Link to="/blog" className="btn-ghost-gold mt-8 inline-flex">
                                {t("blog.nav_back")}
                            </Link>
                        </div>
                    )}

                    {post && (
                        <article data-testid={`blogpost-${post.slug}`}>
                            <div className="mt-10 flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-[#C9A96E]">
                                <span>{post.category}</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-slate-500">
                                    {post.read_time_minutes} {t("blog.read_time")}
                                </span>
                            </div>
                            <h1 className="mt-4 font-serif-display text-white text-4xl sm:text-5xl leading-[1.05]">
                                {post.title}
                            </h1>
                            <div className="mt-6 text-xs text-slate-500 tracking-widest uppercase">
                                {t("blog.by")} {post.author} — {new Date(post.published_at).toLocaleDateString()}
                            </div>
                            {post.featured_image && (
                                <img
                                    src={post.featured_image}
                                    alt={post.title}
                                    className="mt-10 w-full h-72 object-cover rounded-sm border border-[#C9A96E]/15"
                                />
                            )}
                            <div
                                className="prose-luxenova mt-12"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(post.content)) }}
                            />

                            {(post.tags || []).length > 0 && (
                                <div className="mt-16 pt-8 border-t border-[#C9A96E]/15 flex flex-wrap gap-2">
                                    {post.tags.map((tg) => (
                                        <span
                                            key={tg}
                                            className="text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[#C9A96E]/25 text-slate-400"
                                        >
                                            {tg}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    )}
                </div>
            </main>
            <Footer />
            <ChatbotWidget />

            <style>{`
                .prose-luxenova { color: #cbd5e1; font-size: 17px; line-height: 1.8; }
                .prose-luxenova h2 {
                    font-family: 'Playfair Display', serif;
                    color: #fff;
                    font-size: 28px;
                    margin: 2.5em 0 0.6em;
                    line-height: 1.2;
                    letter-spacing: -0.01em;
                }
                .prose-luxenova h3 {
                    font-family: 'Playfair Display', serif;
                    color: #fff;
                    font-size: 22px;
                    margin: 2em 0 0.5em;
                }
                .prose-luxenova p { margin: 1em 0; }
                .prose-luxenova a { color: #C9A96E; text-decoration: underline; }
                .prose-luxenova ul { list-style: none; padding: 0; margin: 1em 0; }
                .prose-luxenova ul li {
                    position: relative;
                    padding-left: 1.5em;
                    margin: 0.5em 0;
                }
                .prose-luxenova ul li::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0.8em;
                    width: 8px;
                    height: 1px;
                    background: #C9A96E;
                }
                .prose-luxenova strong { color: #fff; font-weight: 500; }
                .prose-luxenova em { color: #C9A96E; font-style: italic; }
                .prose-luxenova blockquote {
                    border-left: 2px solid #C9A96E;
                    padding-left: 1.2em;
                    margin: 1.5em 0;
                    font-style: italic;
                    color: #94a3b8;
                }
            `}</style>
        </div>
    );
}

/** Tiny Markdown → HTML renderer good enough for LuxeNova editorial (headings, bold, italic, links, lists, paragraphs). */
function renderMarkdown(md = "") {
    // Escape HTML first
    let s = md
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Headings
    s = s.replace(/^###\s+(.*)$/gm, "<h3>$1</h3>");
    s = s.replace(/^##\s+(.*)$/gm, "<h2>$1</h2>");

    // Bold + italic + links + inline code
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Lists — group consecutive "- " lines
    s = s.replace(/(^|\n)((?:-\s+.+(?:\n|$))+)/g, (m, pfx, block) => {
        const items = block
            .trim()
            .split(/\n/)
            .map((l) => l.replace(/^-\s+/, ""))
            .map((l) => `<li>${l}</li>`)
            .join("");
        return `${pfx}<ul>${items}</ul>`;
    });

    // Blockquotes
    s = s.replace(/^>\s+(.*)$/gm, "<blockquote>$1</blockquote>");

    // Paragraphs — wrap remaining text separated by blank lines
    s = s
        .split(/\n{2,}/)
        .map((block) => {
            if (/^\s*<(h2|h3|ul|blockquote|p)/.test(block)) return block;
            return `<p>${block.replace(/\n/g, " ").trim()}</p>`;
        })
        .join("\n");

    return s;
}
